/* eslint-disable no-redeclare, @typescript-eslint/no-redeclare */
import React, { FC, useEffect, useState } from 'react';
import { Box, Group, Paper, Skeleton, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconChevronDown, IconChevronDownLeft, IconChevronRight, IconExternalLink, IconX } from '@tabler/icons';
import { RoutingS } from '../../Service/RoutingS';
import ResourcePlanningS from '../../Service/restapi/resourcePlanningS';
import ResourceS from '../../Service/ResourceS';
import { EventPlanningGroup, Planning, PlanningState, Shortage } from '../../Types/ResourceT';
import { ThemeText } from '../../Atoms/ThemeText';

type ResourcePreview = {
  key: string;
  resourceId: string;
  resourceName: string;
  quantity: number;
  showResourcePlanningShortage: boolean;
  showWarehousePlanningShortage: boolean;
  hasConfirmedWarehousePlanning: boolean;
  children: ResourcePreview[];
};

type PlanningGroupPreview = {
  id: string;
  name: string;
  interval: { start: Date; end: Date };
  resources: ResourcePreview[];
};

const fmt = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const formatInterval = (interval: { start: Date; end: Date }) => {
  const start = new Date(interval.start);
  const end = new Date(interval.end);
  const startStr = fmt.format(start);
  const endStr =
    start.toDateString() === end.toDateString()
      ? new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(end)
      : fmt.format(end);
  return `${startStr} – ${endStr}`;
};

const openResourceDetails = (resourceId: string) => {
  RoutingS.openInNewTab(`/resources/${resourceId}/details`);
};

const getShortageState = (shortages: Shortage[]) => ({
  showResourcePlanningShortage: shortages.some((shortage) => shortage.showResourcePlanningShortage),
  showWarehousePlanningShortage: shortages.some((shortage) => shortage.showWarehousePlanningShortage),
});

const getResourceShortageLabel = (resource: ResourcePreview) => {
  if (resource.hasConfirmedWarehousePlanning && resource.showWarehousePlanningShortage) {
    return 'Resource ist bestätigt, aber es gibt einen Mangel.';
  }
  if (resource.hasConfirmedWarehousePlanning) {
    return 'Resource ist eingeplant und ausreichend verfügbar';
  }
  if (!resource.hasConfirmedWarehousePlanning && resource.showResourcePlanningShortage) {
    return 'Resource ist nicht ausreichend verfügbar.';
  }
  return 'Resource ist verfügbar und muss noch bestätigt werden.';
};

const ResourcePlanningStatusIcon: FC<{ resource: ResourcePreview }> = ({ resource }) => {
  const hasWarehouseShortage = resource.hasConfirmedWarehousePlanning && resource.showWarehousePlanningShortage;
  const hasResourceShortage = !resource.hasConfirmedWarehousePlanning && resource.showResourcePlanningShortage;

  if (resource.hasConfirmedWarehousePlanning) {
    return (
      <ThemeIcon radius="xl" size="xs" color={hasWarehouseShortage ? '#FF5C5A' : 'green'}>
        {hasWarehouseShortage ? <IconX size={12} /> : <IconCheck size={12} />}
      </ThemeIcon>
    );
  }

  return (
    <ThemeIcon radius="xl" size="xs" color={hasResourceShortage ? '#FFD600' : 'gray'}>
      {hasResourceShortage ? <IconX color="#4b525b" size={12} /> : <IconCheck color="black" size={12} />}
    </ThemeIcon>
  );
};

const buildResourcePreviews = (
  plannings: Planning[],
  confirmedResourcePlanningIds: Set<string>,
  shortagesByResourceId: Map<string, Shortage[]>,
) => {
  const resourcePreviews = plannings
    .map((planning): ResourcePreview | undefined => {
      const resourceId = planning.resource?.id;
      const resourceName = planning.resource?.name;
      if (!resourceId || !resourceName) {
        return undefined;
      }

      const hasConfirmedWarehousePlanning = confirmedResourcePlanningIds.has(planning.id);

      return {
        key: planning.id,
        resourceId,
        resourceName,
        quantity: planning.plannedQuantity ?? 0,
        hasConfirmedWarehousePlanning,
        ...getShortageState(shortagesByResourceId.get(resourceId) ?? []),
        children: buildResourcePreviews(planning.children ?? [], confirmedResourcePlanningIds, shortagesByResourceId),
      };
    })
    .filter((resource): resource is ResourcePreview => !!resource);

  return resourcePreviews.sort((a, b) => {
    if (a.children.length !== b.children.length) {
      return b.children.length - a.children.length;
    }
    return a.resourceName.localeCompare(b.resourceName);
  });
};

const getGroupStatus = (resources: ResourcePreview[]): 'red' | 'yellow' | 'green' => {
  let hasYellow = false;
  for (const resource of resources) {
    const isRed = resource.hasConfirmedWarehousePlanning && resource.showWarehousePlanningShortage;
    const isYellow = !resource.hasConfirmedWarehousePlanning && resource.showResourcePlanningShortage;
    if (isRed) return 'red';
    if (isYellow) hasYellow = true;
    const childStatus = getGroupStatus(resource.children);
    if (childStatus === 'red') return 'red';
    if (childStatus === 'yellow') hasYellow = true;
  }
  return hasYellow ? 'yellow' : 'green';
};

const GroupStatusIcon: FC<{ resources: ResourcePreview[] }> = ({ resources }) => {
  const status = getGroupStatus(resources);
  if (status === 'red') {
    return (
      <ThemeIcon radius="xl" size="xs" color="#FF5C5A">
        <IconX size={12} />
      </ThemeIcon>
    );
  }
  if (status === 'yellow') {
    return (
      <ThemeIcon radius="xl" size="xs" color="#FFD600">
        <IconX color="#4b525b" size={12} />
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon radius="xl" size="xs" color="green">
      <IconCheck size={12} />
    </ThemeIcon>
  );
};

const getOutsideRangeTooltip = (
  interval: { start: Date; end: Date },
  eventStart: Date,
  eventEnd: Date,
): string | null => {
  const start = new Date(interval.start);
  const end = new Date(interval.end);
  const evStart = new Date(eventStart);
  const evEnd = new Date(eventEnd);
  if (start >= evEnd) return 'Planungsgruppe beginnt nach Ende des Events';
  if (end <= evStart) return 'Planungsgruppe endet vor Beginn des Events';
  return null;
};

const PlanningGroupCard: FC<{ group: PlanningGroupPreview; eventId: number; eventStart: Date; eventEnd: Date }> = ({
  group,
  eventId,
  eventStart,
  eventEnd,
}) => {
  const [opened, setOpened] = useState(false);
  const visibleResources = group.resources.slice(0, 5);
  const hiddenResources = group.resources.length - visibleResources.length;
  const outsideRangeTooltip = getOutsideRangeTooltip(group.interval, eventStart, eventEnd);

  return (
    <Paper className="event-list-item" shadow="xs" radius={10} p="xs">
      <Group
        spacing={4}
        noWrap
        onClick={() => setOpened((o) => !o)}
        sx={{ cursor: 'pointer' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {opened ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        </Box>
        <GroupStatusIcon resources={group.resources} />
        <Text size="sm" className="overflow-ellipsis" sx={{ flex: 1, minWidth: 0 }}>
          {group.name}
        </Text>
        {outsideRangeTooltip ? (
          <Tooltip label={outsideRangeTooltip} withArrow position="top">
            <Group spacing={4} sx={{ flexShrink: 0 }} noWrap>
              <Text size="xs" color="red">
                {formatInterval(group.interval)}
              </Text>
              <IconAlertTriangle size={14} color="red" />
            </Group>
          </Tooltip>
        ) : (
          <Text size="xs" color="dimmed" sx={{ flexShrink: 0 }}>
            {formatInterval(group.interval)}
          </Text>
        )}
        <Tooltip label="Ressourcen im Event öffnen" withArrow position="top">
          <Box
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: 'gray' }}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); RoutingS.openInNewTab(`${process.env.REACT_APP_FRONTEND_URL}/events/${eventId}/resources`); }}
          >
            <IconExternalLink size={14} />
          </Box>
        </Tooltip>
      </Group>
      {opened && (
        <Stack mt={8} spacing={4}>
          {visibleResources.map((resource) => (
            <ResourcePreviewRow key={resource.key} resource={resource} />
          ))}
          {hiddenResources > 0 && (
            <Text size="xs" color="dimmed" weight={600}>
              {`+${hiddenResources}`}
            </Text>
          )}
        </Stack>
      )}
    </Paper>
  );
};

const ResourcePreviewRow: FC<{ resource: ResourcePreview; depth?: number }> = ({ resource, depth = 0 }) => {
  const [opened, setOpened] = useState(false);
  const hasChildren = resource.children.length > 0;
  const toggleOpened = () => setOpened((current) => !current);

  return (
    <>
      <Tooltip label={getResourceShortageLabel(resource)}>
        <Group
          spacing={4}
          noWrap
          onClick={() => {
            if (hasChildren) {
              toggleOpened();
            }
          }}
          sx={(theme) => ({
            padding: '3px 8px',
            paddingLeft: 8 + depth * 16,
            borderRadius: theme.radius.sm,
            cursor: 'pointer',
            maxWidth: '100%',
          })}
          className="overflow-ellipsis"
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexShrink: 0,
              justifyContent: 'center',
              width: 18,
            }}
          >
            {hasChildren && opened && (
              <IconChevronDown size={16} />
            )}
            {hasChildren && !opened && (
              <IconChevronRight size={16} />
            )}
            {!hasChildren && depth > 0 && <IconChevronDownLeft size={16} />}
          </Box>
          <ResourcePlanningStatusIcon resource={resource} />
          <Text size="xs" className="overflow-ellipsis">
            {`${resource.resourceName} `}
            <Text span weight={400}>
              {`(${resource.quantity})`}
            </Text>
          </Text>
        </Group>
      </Tooltip>
      {opened &&
        resource.children.map((child) => (
          <ResourcePreviewRow key={child.key} resource={child} depth={depth + 1} />
        ))}
    </>
  );
};

const EventResourcePlanningPreview: FC<{ eventId: number; maxWidth: number; eventStart: Date; eventEnd: Date }> = ({
  eventId,
  maxWidth: _maxWidth,
  eventStart,
  eventEnd,
}) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<PlanningGroupPreview[]>([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const abortController = new AbortController();

    const fetch = async () => {
      const [timelinePlannings, timelineShortages] = await Promise.all([
        ResourceS.fetchTimelinePlannings([eventId], abortController.signal),
        ResourcePlanningS.getTimelineShortages([eventId], abortController.signal),
      ]);
      const planningGroups = timelinePlannings?.[eventId] ?? [];
      const timelineShortagesForEvent = timelineShortages[`${eventId}`];
      const shortages = timelineShortagesForEvent?.shortages ?? [];
      const confirmedResourcePlanningIds = new Set(
        (timelineShortagesForEvent?.warehousePlannings ?? [])
          .filter((warehousePlanning) => warehousePlanning.state === PlanningState.CONFIRMED)
          .map((warehousePlanning) => warehousePlanning.resourcePlanningId),
      );
      const shortagesByResourceId = shortages.reduce((accumulator, shortage) => {
        const resourceShortages = accumulator.get(shortage.resourceId) ?? [];
        resourceShortages.push(shortage);
        accumulator.set(shortage.resourceId, resourceShortages);
        return accumulator;
      }, new Map<string, Shortage[]>());
      if (!active) {
        return;
      }
      if (!planningGroups || planningGroups.length === 0) {
        setGroups([]);
        return;
      }

      const previews = await Promise.all(
        planningGroups.map(async (group: EventPlanningGroup) => {
          const plannings = await ResourcePlanningS.fetchPlannings(group.id);
          const resourcePreviews = buildResourcePreviews(
            plannings,
            confirmedResourcePlanningIds,
            shortagesByResourceId,
          );

          return {
            id: group.id,
            name: group.name,
            interval: group.interval,
            resources: resourcePreviews,
          };
        }),
      );

      if (active) {
        const evStart = new Date(eventStart).getTime();
        const evEnd = new Date(eventEnd).getTime();
        setGroups(
          previews.filter(
            (group) =>
              group.resources.length > 0 &&
              new Date(group.interval.start).getTime() < evEnd &&
              new Date(group.interval.end).getTime() > evStart,
          ),
        );
      }
    };

    fetch().finally(() => {
      if (active) {
        setLoading(false);
      }
    });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [eventId]);

  if (loading) {
    return <Skeleton mt={6} height={40} width={220} />;
  }

  if (groups.length === 0) {
    return null;
  }

  return (
    <Stack spacing={8}>
      {groups.map((group) => (
        <PlanningGroupCard key={group.id} group={group} eventId={eventId} eventStart={eventStart} eventEnd={eventEnd} />
      ))}
    </Stack>
  );
};

export default EventResourcePlanningPreview;
